import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { SavingPlanSuggestion, SavingPlanSuggestionSegment, SavingPlanSuggestionsService } from '@zero/saving-plans/domain';
import { SuggestionPercentagePipe } from '@zero/saving-plans/ui-pipes';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { ChartConfiguration } from 'chart.js';
import { commonRoutes } from 'constants/web-app-constants';
import { BaseChartDirective } from 'ng2-charts';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { SavingPlanSuggestionItemComponent } from '../saving-plan-suggestion-item/saving-plan-suggestion-item.component';

@Component({
  selector: 'saving-plans-saving-plan-suggestions',
  templateUrl: './saving-plan-suggestions.component.html',
  styleUrls: ['./saving-plan-suggestions.component.scss'],
  imports: [
    BaseChartDirective,
    SavingPlanSuggestionItemComponent,
    AsyncPipe,
    TranslocoPipe,
    SuggestionPercentagePipe,
    RouterLink,
    ButtonComponent,
  ],
})
export class SavingPlanSuggestionsComponent {
  readonly #service = inject(SavingPlanSuggestionsService);

  public readonly savingsPlanForm = commonRoutes.postlogin.add_savings_plan;

  #activeSegment = new BehaviorSubject<SavingPlanSuggestionSegment>('300');
  public readonly activeSegment$ = this.#activeSegment.asObservable();

  public activeSuggestions$: Observable<SavingPlanSuggestion[]> = this.activeSegment$.pipe(
    map((suggestion) => this.#service.getSuggestions(suggestion)),
  );

  public multiSavingsPlanQueryParams$ = combineLatest([this.activeSuggestions$, this.activeSegment$]).pipe(
    map(([suggestion, activeSegment]) => ({
      totalAmount: activeSegment,
      isin: suggestion.map(({ isin }) => isin).join(','),
      share: suggestion.map(({ amount }) => amount.toString()).join(','),
    })),
  );

  public doughnutChartDatasets$: Observable<ChartConfiguration<'doughnut'>['data']['datasets']> = combineLatest([
    this.activeSuggestions$,
    this.activeSegment$,
  ]).pipe(
    map(([suggestion, activeSegment]) => [
      {
        data: activeSegment !== '1' ? suggestion.flatMap(({ amount }) => amount) : [1],
        backgroundColor: this.suggestionColors,
        borderWidth: 0,
      },
    ]),
  );

  public readonly doughnutChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    plugins: {
      tooltip: {
        enabled: false,
      },
    },
  };

  public readonly suggestionColors: string[] = ['#248eff', '#02172D', '#E0146A', '#D3C398', '#00CED1'];

  public changeSelection(suggestion: SavingPlanSuggestionSegment) {
    this.#activeSegment.next(suggestion);
  }
}
